import { Language } from 'src/api/language'
import { useTranslation } from 'react-i18next'
import { CityTaxExonerationCode, CustomerDetailFragment, CustomerType, GenderType } from 'src/api/api.generated'
import { isEmail, isPhoneNumber } from 'src/utils/contact'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { CustomerFormData, PersonFormData } from '../customer.form.types'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { TFunction } from 'i18next'
import { areIntervalsOverlapping } from 'date-fns'

const usePersonForm = ({ customer, onSubmit }: { customer: CustomerDetailFragment | null; onSubmit: (data: CustomerFormData) => Promise<void> }) => {
  const { t } = useTranslation()
  const tenant = useTenant()

  const personForm = getPersonSchema(t)

  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
  } = useForm<PersonForm>({ resolver: zodResolver(personForm) })

  const submitHandler: SubmitHandler<PersonForm> = useCallback(
    async (data: PersonForm) => {
      const personFormData: PersonFormData = {
        type: CustomerType.PERSON,
        lastName: data.lastName,
        firstName: data.firstName,
        name: `${data.lastName} ${data.firstName}`,
        language: data.language,
        privacy: {
          dataThirdPartyConsent: data.privacyDataThirdParty.consent,
          dataThirdPartyConsentDate: data.privacyDataThirdParty.date,
          managementConsent: data.privacyManagement.consent,
          managementConsentDate: data.privacyManagement.date,
          marketingConsent: data.privacyMarketing.consent,
          marketingConsentDate: data.privacyMarketing.date,
          profiledMarketingConsent: data.privacyProfiledMarketing.consent,
          profiledMarketingConsentDate: data.privacyProfiledMarketing.date,
        },
        mailContacts: data.mailContact ? [{ isDefault: true, value: data.mailContact, type: 'MAIN', order: 1 }] : [],
        phoneContacts: data.phoneContact ? [{ isDefault: true, value: data.phoneContact, type: 'MAIN', order: 1 }] : [],
        gender: data.gender,
        address: data.address || null,
        birthDate: data.birthDate,
        fiscalCode: data.fiscalCode,
        birthAddress: data.birthAddress || null,
        nationalityId: data.nationalityId || null,
        identificationDocument: data.identificationDocument,
        tagsId: data.tagsId,
        relatedCompanyId: data.relatedCompanyId,
        cityTax:
          data.exoneration || (data.contracts?.length ?? 0) > 0
            ? {
                exoneration: data.exoneration || null,
                contracts: (data.contracts?.length ?? 0) > 0 ? data.contracts : null,
              }
            : null,
      }
      await onSubmit(personFormData)
    },
    [onSubmit],
  )

  const onFormSubmit = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  useEffect(() => {
    if (customer) {
      setValue('firstName', customer.firstName || '', { shouldDirty: false })
      setValue('lastName', customer.lastName || '', { shouldDirty: false })

      setValue('mailContact', customer.mailContacts && customer.mailContacts.length > 0 ? customer.mailContacts[0].value : '', { shouldDirty: false })
      setValue('phoneContact', customer.phoneContacts && customer.phoneContacts.length > 0 ? customer.phoneContacts[0].value : '', { shouldDirty: false })
      setValue(
        'privacyDataThirdParty',
        {
          consent: customer.privacy?.dataThirdPartyConsent || false,
          date: customer.privacy?.dataThirdPartyConsentDate || undefined,
        },
        { shouldDirty: false },
      )
      setValue(
        'privacyMarketing',
        {
          consent: customer.privacy?.marketingConsent || false,
          date: customer.privacy?.marketingConsentDate || undefined,
        },
        { shouldDirty: false },
      )
      setValue(
        'privacyManagement',
        {
          consent: customer.privacy?.managementConsent || false,
          date: customer.privacy?.managementConsentDate || undefined,
        },
        { shouldDirty: false },
      )
      setValue(
        'privacyProfiledMarketing',
        {
          consent: customer.privacy?.profiledMarketingConsent || false,
          date: customer.privacy?.profiledMarketingConsentDate || undefined,
        },
        { shouldDirty: false },
      )
      setValue('language', customer.language ?? tenant.language, { shouldDirty: false })
      setValue('gender', customer.person?.gender || undefined, { shouldDirty: false })
      setValue(
        'address',
        customer.address
          ? {
              countryId: customer.address.countryId || null,
              address: customer.address.address || null,
              city: customer.address.city || null,
              municipalityId: customer.address.municipalityId || null,
              postalCode: customer.address.postalCode || null,
              province: customer.address.province || null,
            }
          : null,
        { shouldDirty: false },
      )
      setValue('birthDate', customer.person?.birthDate || undefined, { shouldDirty: false })
      setValue('fiscalCode', customer.fiscalCode || undefined, { shouldDirty: false })
      setValue(
        'birthAddress',
        customer.person?.birthAddress
          ? {
              countryId: customer.person.birthAddress.countryId || null,
              address: customer.person.birthAddress.address || null,
              city: customer.person.birthAddress.city || null,
              municipalityId: customer.person.birthAddress.municipalityId || null,
              postalCode: customer.person.birthAddress.postalCode || null,
              province: customer.person.birthAddress.province || null,
            }
          : null,
        { shouldDirty: false },
      )
      setValue('nationalityId', customer.person?.nationalityId || null, { shouldDirty: false })
      setValue(
        'identificationDocument',
        customer.person?.identificationDocument
          ? {
              documentId: customer.person.identificationDocument.documentId,
              number: customer.person.identificationDocument.number,
              releaseBy: customer.person.identificationDocument.releaseBy || undefined,
              expirationDate: customer.person.identificationDocument.expirationDate,
              countryId: customer.person.identificationDocument.countryId,
              city: customer.person.identificationDocument.city || undefined,
            }
          : undefined,
        { shouldDirty: false },
      )
      setValue('tagsId', customer.tagsId || undefined, { shouldDirty: false })
      setValue('exoneration', customer?.cityTax?.exoneration ?? null, { shouldDirty: false })
      setValue('contracts', customer?.cityTax?.contracts ?? null, { shouldDirty: false })
      setValue('relatedCompanyId', customer?.relatedCompanyId ?? null, { shouldDirty: false })
    } else {
      setValue('privacyDataThirdParty', { consent: false }, { shouldDirty: false })
      setValue('privacyProfiledMarketing', { consent: false }, { shouldDirty: false })
      setValue('privacyManagement', { consent: false }, { shouldDirty: false })
      setValue('privacyMarketing', { consent: false }, { shouldDirty: false })
      setValue('address', null, { shouldDirty: false })
      setValue('birthAddress', null, { shouldDirty: false })
      setValue('nationalityId', null, { shouldDirty: false })
      setValue('language', tenant.language, { shouldDirty: false })
      setValue('exoneration', null, { shouldDirty: false })
      setValue('contracts', null, { shouldDirty: false })
      setValue('relatedCompanyId', null, { shouldDirty: false })
    }
  }, [customer?.id])

  const formValue = watch()

  return { personForm, onFormSubmit, control, errors, formValue, setValue, isDirty }
}

export default usePersonForm

function getPersonSchema(t?: TFunction) {
  return z.object({
    lastName: z.string().min(1),
    firstName: z.string().min(1),
    mailContact: z
      .string()
      .optional()
      .superRefine((data, ctx) => {
        if (!data) return
        if (!isEmail(data)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.invalid_email') : 'Invalid email',
          })
        }
      }),
    phoneContact: z
      .string()
      .optional()
      .superRefine((data, ctx) => {
        if (!data) return
        if (!isPhoneNumber(data)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.invalid_phone_number') : 'Invalid phone number',
          })
        }
      }),
    privacyMarketing: z.object({
      consent: z.boolean(),
      date: z.date().optional(),
    }),
    privacyDataThirdParty: z.object({
      consent: z.boolean(),
      date: z.date().optional(),
    }),
    privacyManagement: z.object({
      consent: z.boolean(),
      date: z.date().optional(),
    }),
    privacyProfiledMarketing: z.object({
      consent: z.boolean(),
      date: z.date().optional(),
    }),
    language: z.enum(Language),
    gender: z.nativeEnum(GenderType).optional(),
    address: z
      .object({
        countryId: z.string().nullable(),
        address: z.string().nullable(),
        city: z.string().nullable(),
        municipalityId: z.string().nullable(),
        postalCode: z.string().nullable(),
        province: z.string().nullable(),
      })
      .nullable()
      .optional(),
    birthDate: z.date().optional(),
    fiscalCode: z
      .string()
      .optional()
      .transform((fiscalCode) => (fiscalCode ? fiscalCode?.trim() : fiscalCode)),
    birthAddress: z
      .object({
        countryId: z.string().nullable(),
        address: z.string().nullable(),
        city: z.string().nullable(),
        municipalityId: z.string().nullable(),
        postalCode: z.string().nullable(),
        province: z.string().nullable(),
      })
      .nullable()
      .optional(),
    nationalityId: z.string().nullable(),
    identificationDocument: z
      .object({
        documentId: z.string().min(1),
        number: z.string().min(1),
        releaseBy: z.string().optional(),
        expirationDate: z.date(),
        countryId: z.string().min(1),
        city: z.string().optional(),
      })
      .optional(),
    tagsId: z.string().min(1).array().optional(),
    exoneration: z.nativeEnum(CityTaxExonerationCode).nullable().default(null),
    relatedCompanyId: z.string().nullable(),
    contracts: z
      .array(
        z.object({
          end: z.date().nullable().optional(),
          start: z.date().nullable().optional(),
        }),
      )
      .nullable()
      .superRefine((contracts, context) => {
        if (!contracts) return

        for (let i = 0; i < contracts.length; i++) {
          const currentContract = contracts[i]

          if (!currentContract.start || !currentContract.end) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: [i, 'start'],
              message: t ? t('pms.common.customer.contracts.error.dates') : 'dates must be defined',
            })

            return z.NEVER
          }

          const nextContract = contracts[i + 1]
          if (
            currentContract &&
            currentContract.end &&
            currentContract.start &&
            nextContract &&
            nextContract?.end &&
            nextContract?.start &&
            areIntervalsOverlapping({ start: currentContract.start, end: currentContract.end }, { start: nextContract.start, end: nextContract.end }, { inclusive: true })
          ) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: [i, 'start'],
              message: t ? t('pms.common.customer.contracts.error.not_overlap') : 'contracts cannot overlap',
            })

            return z.NEVER
          }
        }
      }),
  })
}

const personSchema = getPersonSchema()
export type PersonForm = z.infer<typeof personSchema>
